import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
}

export default function SEOHead({ title, description, keywords, canonical, ogImage, schema }: SEOHeadProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (selector: string, attr: string, attrValue: string, content: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('meta[name="description"]', 'name', 'description', description);

    if (keywords) {
      setMeta('meta[name="keywords"]', 'name', 'keywords', keywords);
    }

    if (canonical) {
      let linkEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!linkEl) {
        linkEl = document.createElement('link');
        linkEl.setAttribute('rel', 'canonical');
        document.head.appendChild(linkEl);
      }
      linkEl.setAttribute('href', canonical);
    }

    const ogData = [
      { prop: 'og:title', content: title },
      { prop: 'og:description', content: description },
      { prop: 'og:url', content: canonical || window.location.href },
    ];
    if (ogImage) ogData.push({ prop: 'og:image', content: ogImage });

    ogData.forEach(({ prop, content }) => {
      setMeta(`meta[property="${prop}"]`, 'property', prop, content);
    });

    const twData = [
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ];
    if (ogImage) twData.push({ name: 'twitter:image', content: ogImage });

    twData.forEach(({ name, content }) => {
      setMeta(`meta[name="${name}"]`, 'name', name, content);
    });

    let scriptEl = document.getElementById('dynamic-schema') as HTMLScriptElement | null;
    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.id = 'dynamic-schema';
      scriptEl.type = 'application/ld+json';
      document.head.appendChild(scriptEl);
    }

    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      const graphData =
        schemas.length > 1
          ? { '@context': 'https://schema.org', '@graph': schemas }
          : { '@context': 'https://schema.org', ...schemas[0] };
      scriptEl.textContent = JSON.stringify(graphData);
    } else {
      scriptEl.textContent = '';
    }
  }, [title, description, keywords, canonical, ogImage, schema]);

  return null;
}